import React, { useEffect, useState, useRef } from "react";
import "./TimeTrackingV2Screen.css";
import { supabase } from "../supabase";
import { getActiveProjects } from "../helpers/projectVisibility";
import { getScopedCachedProjects } from "../helpers/offlineCacheScope";
import { isOffline } from "../helpers/connectivity";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import {
  applyActiveTimelogFilter,
  archiveTimeLog,
  deleteTimeLog,
  filterTimeLogs,
  groupTimeLogsByEmployee,
  TIME_LOG_DATE_RANGE_OPTIONS,
} from "../helpers/timeLogVisibility";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { useDashboardCarouselSwipe } from "../hooks/useDashboardCarouselSwipe";
import { applyQuoteLineLinkToTimelogRow } from "../helpers/projectLabourLedger";
import {
  resolveManualTimelogProjectFields,
  runWithSavingState,
} from "../helpers/manualTimelogSave";
import { toNullableUuid } from "../helpers/uuidFields";
import {
  actorFromTimelogRow,
  resolveTimelogEmployeeSnapshot,
  snapshotToTimelogFields,
} from "../helpers/employeeSnapshot";
import { usePermissions } from "../hooks/usePermissions";
import { useOrganization } from "../context/OrganizationContext";
import { CAPABILITY, DATA_SCOPE } from "../helpers/permissions";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import {
  assertProjectOperationallyWritable,
  CP_CLIENT_ARCHIVED_EVENT,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "../helpers/operationalLifecycle";
import { orgScopedInsert } from "../helpers/orgQueryScope";
import {
  applyTimelogHubScope,
  canUseTimelogHubScope,
  isUserIdInCapabilityScope,
  loadScopedUsers,
  scopeOperationalTimeLogsForActor,
  TIMELOG_HUB_SCOPE,
} from "../helpers/scopedQueries";
import {
  formatTimeInputValue,
  resolveManualTimelogBounds,
  sessionBoundsAlertMessage,
  toLocalDateInputValue,
} from "../helpers/dateTimeHelpers";
import {
  TIME_LOG_PAYROLL_TYPE_OPTIONS,
} from "../helpers/payrollHelpers";
import { loadPayrollTypeSessionOptions } from "../helpers/catalogs/payrollTypeCatalog";
import {
  NO_WORK_TYPES_MESSAGE,
  SELECT_WORK_TYPE_MESSAGE,
  loadWorkTypesForSession,
  readInitialSessionDefaultWorkType,
  readInitialSessionWorkTypeOptions,
  resolveDefaultWorkTypeName,
  resolveSessionWorkType,
  workTypesToSessionOptions,
} from "../helpers/catalogs/workTypeCatalog";
import { TimeLogList } from "./timelogs/TimeLogList";
import { TimeLogsDesktop } from "./timelogs/TimeLogsDesktop";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { TimeLogDetails } from "./timelogs/TimeLogDetails";
import { TimeLogEditor } from "./timelogs/TimeLogEditor";
import {
  TimeLogsActionBar,
  TimeLogsActiveFilters,
  TimeLogsFilterSheet,
  TimeLogsScopeTabs,
  TimeLogsSearchRow,
} from "./timelogs/TimeLogsToolbar";
import { pluralLogs, summarizeLogs } from "./timelogs/timeLogModel";
import {
  cpModalOverlayStyle,
  cpModalPanelStyle,
} from "./shared/crewpilotModalStyles";
import { ViewportPortal } from "./shared/ViewportDialog";

const WORK_TYPE_PLACEHOLDER_OPTION = { value: "", label: "Select Work Type" };

/**
 * Work Type choices for the time log form: the organization's active Work
 * Types. When editing, a historical value that is no longer active (or a
 * legacy label) stays selectable so saving an edit never rewrites it.
 */
export function buildTimeLogWorkTypeOptions(activeOptions, currentValue, mode) {
  const options = [...(activeOptions || [])];
  const value = String(currentValue || "").trim();
  if (mode === "edit" && value && !options.some((option) => option.value === value)) {
    options.push({ value, label: value });
  }
  if (!options.length || (mode === "edit" && !value)) {
    return [WORK_TYPE_PLACEHOLDER_OPTION, ...options];
  }
  return options;
}

const TEAM_EMPLOYEE_SELECT =
  "supabase_user_id, employee_name, trade_id, trade_name, classification_name, payroll_type, hourly_rate";

function resolveDefaultPayrollType(actor = {}) {
  const fromActor = (actor.payroll_type || "").trim();
  if (fromActor) {
    return fromActor;
  }

  return TIME_LOG_PAYROLL_TYPE_OPTIONS[0].value;
}

function computeNetHoursPreview({
  startDateIso,
  endDateIso,
  startTime,
  endTime,
  breakMinutes,
  originalLog,
}) {
  if (!startDateIso || !endDateIso || !startTime || !endTime) {
    return null;
  }

  const { start, end, isValid } = resolveManualTimelogBounds({
    startDateIso,
    endDateIso,
    startTime,
    endTime,
    originalLog: originalLog || null,
  });

  if (!isValid) {
    return null;
  }

  const grossSeconds = Math.floor((end - start) / 1000);
  const breakSeconds = Math.max(0, Number(breakMinutes || 0) * 60);
  const netSeconds = Math.max(0, grossSeconds - breakSeconds);

  return Number((netSeconds / 3600).toFixed(2));
}

const MODAL_OVERLAY_STYLE = cpModalOverlayStyle({
  background: "rgba(0,0,0,0.65)",
  padding: "24px",
  zIndex: 10000,
});

const MODAL_PANEL_STYLE = cpModalPanelStyle({
  background: "var(--nx-surface, #0f1729)",
  padding: "24px",
});

function resolveHubProjectFilter(projects, initialProjectId, initialProjectName) {
  if (!projects?.length) {
    return "";
  }

  if (initialProjectId) {
    const byId = projects.find((project) => project.project_id === initialProjectId);
    if (byId) {
      return initialProjectId;
    }
  }

  const normalizedName = (initialProjectName || "").trim().toLowerCase();
  if (normalizedName) {
    const byName = projects.find(
      (project) =>
        (project.project_name || "").trim().toLowerCase() === normalizedName,
    );
    if (byName?.project_id) {
      return byName.project_id;
    }
  }

  return "";
}

export function ManualTimeEntryScreen({
  onBack,
  session,
  currentUser,
  initialProjectId = null,
  initialProjectName = null,
  initialDateRangeFilter = null,
  initialView = "hub",
  onCarouselNavigate,
  onOpenNavigator,
  // Opened for one record from another screen (Project → Labour). The log is
  // resolved by its id; leaving the record returns to that caller.
  initialEditLogId = null,
  onReturnToCaller = null,
}) {
  const { can, scope, directReportIds } = usePermissions();
  const { settings } = useOrganization();
  const timelogScope = scope(CAPABILITY.TIMELOG_VIEW);
  const showEmployeeFilter =
    timelogScope === DATA_SCOPE.TEAM ||
    timelogScope === DATA_SCOPE.ORGANIZATION;
  const showOrgScope = timelogScope === DATA_SCOPE.ORGANIZATION;
  const canEditTeamLogs = can(CAPABILITY.TIMELOG_EDIT);
  const canDeleteLogs = can(CAPABILITY.TIMELOG_DELETE);

  const touchStartX = useRef(null);
  const isDesktopViewport = useIsDesktopViewport();
  const [view, setView] = useState(initialView === "create" ? "form" : "hub");
  const [mode, setMode] = useState("create");
  const [formReturnView, setFormReturnView] = useState("hub");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [logScope, setLogScope] = useState("my_time");
  const [hubProjectFilter, setHubProjectFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState(
    initialDateRangeFilter || "this_month",
  );
  const [hubEmployeeFilter, setHubEmployeeFilter] = useState("");
  const [teamEmployees, setTeamEmployees] = useState([]);

  const [projects, setProjects] = useState([]);
  const [formProjectId, setFormProjectId] = useState("");
  const [formEmployeeId, setFormEmployeeId] = useState("");
  const [formEmployeeName, setFormEmployeeName] = useState("");
  const [formPayrollType, setFormPayrollType] = useState(
    TIME_LOG_PAYROLL_TYPE_OPTIONS[0].value,
  );
  const [payrollTypeOptions, setPayrollTypeOptions] = useState(
    () => [...TIME_LOG_PAYROLL_TYPE_OPTIONS],
  );
  const [formTradeName, setFormTradeName] = useState("");
  const [formClassificationName, setFormClassificationName] = useState("");
  const [formHourlyRate, setFormHourlyRate] = useState(0);
  const [rateResolving, setRateResolving] = useState(false);
  const [startDateIso, setStartDateIso] = useState(() =>
    toLocalDateInputValue(new Date()),
  );
  const [endDateIso, setEndDateIso] = useState(() =>
    toLocalDateInputValue(new Date()),
  );
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("0");
  const [workCompleted, setWorkCompleted] = useState("");
  // Explicit choice only ("" = none). New entries resolve to the org default.
  const [workCategory, setWorkCategory] = useState("");
  const [workTypeOptions, setWorkTypeOptions] = useState(() =>
    readInitialSessionWorkTypeOptions(currentUser),
  );
  const [defaultWorkTypeName, setDefaultWorkTypeName] = useState(() =>
    readInitialSessionDefaultWorkType(currentUser),
  );
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeLogs, setTimeLogs] = useState([]);
  const [selectedTimeLog, setSelectedTimeLog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const initialProjectFilterAppliedRef = useRef(false);
  const canAssignEmployeeOnCreate = showEmployeeFilter && canEditTeamLogs;
  const fromCaller = Boolean(initialEditLogId && onReturnToCaller);
  // "loading" | "ready" | "unavailable" while a caller-requested log resolves.
  const [callerLogState, setCallerLogState] = useState(
    initialEditLogId ? "loading" : "ready",
  );
  const [detailsReadOnly, setDetailsReadOnly] = useState(false);

  // Edit follows timelog.edit and its scope (Self / Team / Org); a log the
  // actor may only view opens in the read-only details view.
  function canEditTimeLogRecord(log) {
    if (!log || !can(CAPABILITY.TIMELOG_EDIT)) return false;
    const ownerId = log.supabase_user_id || log.user_id || "";
    if (!ownerId) return false;
    return isUserIdInCapabilityScope(currentUser, ownerId, CAPABILITY.TIMELOG_EDIT, {
      directReportIds,
    });
  }

  function returnToCaller() {
    setShowDeleteConfirm(false);
    onReturnToCaller?.();
  }

  useEffect(() => {
    function onArchived(event) {
      const clientId = event.detail?.clientId;
      if (!clientId) return;
      setProjects((current) => {
        const removed = new Set(
          current.filter((p) => p.client_id === clientId).map((p) => p.project_id),
        );
        queueMicrotask(() => {
          setFormProjectId((id) => (removed.has(id) ? "" : id));
        });
        return current.filter((p) => p.client_id !== clientId);
      });
    }
    window.addEventListener(CP_CLIENT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(CP_CLIENT_ARCHIVED_EVENT, onArchived);
  }, []);

  useEffect(() => {
    loadProjects();
    loadTeamEmployees();
  }, [session?.user?.id, currentUser?.supabase_user_id, directReportIds]);

  useEffect(() => {
    let cancelled = false;

    async function loadPayrollOptions() {
      const options = await loadPayrollTypeSessionOptions(supabase, currentUser);
      if (!cancelled) {
        setPayrollTypeOptions(options);
      }
    }

    loadPayrollOptions();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.organization_id, currentUser?.supabase_user_id]);

  useEffect(() => {
    let cancelled = false;

    async function loadWorkTypeOptions() {
      const rows = await loadWorkTypesForSession(supabase, currentUser);
      if (!cancelled) {
        setWorkTypeOptions(workTypesToSessionOptions(rows));
        setDefaultWorkTypeName(resolveDefaultWorkTypeName(rows));
      }
    }

    loadWorkTypeOptions();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.organization_id, currentUser?.supabase_user_id]);

  useEffect(() => {
    if (initialProjectFilterAppliedRef.current) {
      return;
    }

    if (!initialProjectId && !initialProjectName) {
      initialProjectFilterAppliedRef.current = true;
      return;
    }

    if (!projects.length) {
      return;
    }

    const resolvedFilter = resolveHubProjectFilter(
      projects,
      initialProjectId,
      initialProjectName,
    );

    if (resolvedFilter) {
      setHubProjectFilter(resolvedFilter);
    }

    initialProjectFilterAppliedRef.current = true;
  }, [projects, initialProjectId, initialProjectName]);

  useEffect(() => {
    loadTimeLogs();
  }, [
    logScope,
    hubEmployeeFilter,
    session?.user?.id,
    currentUser?.supabase_user_id,
    directReportIds,
  ]);

  useEffect(() => {
    if (!hubEmployeeFilter) {
      return;
    }

    if (!canViewEmployee(hubEmployeeFilter)) {
      setHubEmployeeFilter("");
    }
  }, [hubEmployeeFilter, teamEmployees, directReportIds]);

  function canViewEmployee(employeeUserId) {
    return isUserIdInCapabilityScope(
      currentUser,
      employeeUserId,
      CAPABILITY.TIMELOG_VIEW,
      { directReportIds },
    );
  }

  async function loadTeamEmployees() {
    if (shouldSkipSupabase(currentUser)) {
      setTeamEmployees([]);
      return;
    }

    if (!showEmployeeFilter) {
      setTeamEmployees([]);
      return;
    }

    try {
      const data = await loadScopedUsers(
        supabase,
        currentUser,
        CAPABILITY.TIMELOG_VIEW,
        TEAM_EMPLOYEE_SELECT,
        { directReportIds },
      );
      setTeamEmployees(data);
    } catch (error) {
      console.error("TEAM EMPLOYEE LOAD ERROR:", error);
      setTeamEmployees([]);
    }
  }

  async function loadProjects() {
    const userId = session?.user?.id;

    if (!userId) {
      return;
    }

    if (isOffline()) {
      setProjects(getActiveProjects(getScopedCachedProjects(currentUser, directReportIds)));
      return;
    }

    try {
      const data = await loadScopedActiveProjects(
        supabase,
        currentUser,
        { directReportIds },
      );
      setProjects(getActiveProjects(data || []));
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
      setProjects(getActiveProjects(getScopedCachedProjects(currentUser, directReportIds)));
    }
  }

  async function loadTimeLogs() {
    if (shouldSkipSupabase(currentUser)) {
      setTimeLogs([]);
      return;
    }
    if (!session?.user?.id) {
      return;
    }

    let query = supabase
      .from("time_logs")
      .select("*")
      .order("date_iso", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(250);

    query = applyTimelogHubScope(
      query,
      currentUser,
      logScope,
      hubEmployeeFilter,
      { directReportIds },
    );

    query = await scopeOperationalTimeLogsForActor(query, supabase, currentUser, {
      directReportIds,
    });

    const { data, error } = await query;

    if (error) {
      console.error("TIME LOG LOAD ERROR:", error);
      return;
    }

    setTimeLogs(data || []);
  }

  const showScopeSelector = !hubEmployeeFilter && showEmployeeFilter;
  const shouldGroupByEmployee =
    logScope === TIMELOG_HUB_SCOPE.MY_TEAM && !hubEmployeeFilter;

  const filteredLogs = filterTimeLogs(timeLogs, {
    search: searchTerm,
    projectId: hubProjectFilter,
    dateRange: dateRangeFilter,
    orgSettings: settings,
  });
  const groupedLogs = shouldGroupByEmployee
      ? groupTimeLogsByEmployee(filteredLogs)
      : [["", filteredLogs]];

  function resolveCreateEmployeeActor(employeeId) {
    const selfId = currentUser?.supabase_user_id || session?.user?.id || "";
    const selectedId = employeeId || selfId;

    if (selectedId && selectedId === selfId) {
      return currentUser;
    }

    const teamMember = teamEmployees.find(
      (employee) => employee.supabase_user_id === selectedId,
    );

    return teamMember || currentUser;
  }

  function applySnapshotDisplayFields(actor = {}, payrollTypeOverride = null) {
    setFormEmployeeId(actor.supabase_user_id || actor.user_id || "");
    setFormEmployeeName(actor.employee_name || actor.employee || "");
    setFormTradeName(actor.trade_name || "");
    setFormClassificationName(actor.classification_name || "");
    setFormPayrollType(
      payrollTypeOverride || resolveDefaultPayrollType(actor),
    );
    setFormHourlyRate(Number(actor.hourly_rate) || 0);
  }

  function resetFormFields() {
    setSelectedTimeLog(null);
    setFormProjectId("");
    applySnapshotDisplayFields(currentUser);
    const todayIso = toLocalDateInputValue(new Date());
    setStartDateIso(todayIso);
    setEndDateIso(todayIso);
    setStartTime("");
    setEndTime("");
    setBreakMinutes("0");
    setWorkCompleted("");
    setWorkCategory("");
    setNotes("");
  }

  function populateFormFromLog(log) {
    setSelectedTimeLog(log);
    setFormProjectId(log.project_id || "");
    setFormEmployeeId(log.supabase_user_id || log.user_id || "");
    setFormEmployeeName(log.employee || "");
    setFormTradeName(log.trade_name || "");
    setFormClassificationName(log.classification_name || "");
    setFormPayrollType(resolveDefaultPayrollType(log));
    setFormHourlyRate(Number(log.hourly_rate) || 0);
    const startDate = log.start_time ? new Date(log.start_time) : null;
    const endDate = log.end_time ? new Date(log.end_time) : null;

    setStartDateIso(
      (startDate && toLocalDateInputValue(startDate)) || log.date_iso || "",
    );
    setEndDateIso(
      (endDate && toLocalDateInputValue(endDate)) ||
        (startDate && toLocalDateInputValue(startDate)) ||
        log.date_iso ||
        "",
    );
    setStartTime(startDate ? formatTimeInputValue(startDate) : "");
    setEndTime(endDate ? formatTimeInputValue(endDate) : "");

    setBreakMinutes(String(Math.floor((log.break_seconds || 0) / 60)));
    setWorkCompleted(log.work_completed || "");
    // Keep the stored value as-is; a blank historical log stays blank.
    setWorkCategory(log.work_category || "");
    setNotes(log.notes || "");
  }

  function handleCreateEmployeeChange(employeeId) {
    const actor = resolveCreateEmployeeActor(employeeId);
    applySnapshotDisplayFields(actor);
  }

  useEffect(() => {
    if (view !== "form") {
      return;
    }

    let cancelled = false;

    async function refreshResolvedRate() {
      const actor =
        mode === "edit" && selectedTimeLog
          ? actorFromTimelogRow(selectedTimeLog)
          : resolveCreateEmployeeActor(formEmployeeId);

      if (!actor?.supabase_user_id && !actor?.user_id && !session?.user?.id) {
        return;
      }

      setRateResolving(true);

      try {
        const snapshot = await resolveTimelogEmployeeSnapshot(actor, {
          supabase,
          payrollType: formPayrollType,
          supabaseUserId:
            actor.supabase_user_id ||
            actor.user_id ||
            session?.user?.id ||
            null,
        });

        if (!cancelled) {
          setFormHourlyRate(Number(snapshot.hourly_rate) || 0);
          if (mode === "create") {
            setFormTradeName(snapshot.trade_name || formTradeName);
            setFormClassificationName(
              snapshot.classification_name || formClassificationName,
            );
          }
        }
      } catch (error) {
        console.error("TIME LOG RATE RESOLVE ERROR:", error);
      } finally {
        if (!cancelled) {
          setRateResolving(false);
        }
      }
    }

    refreshResolvedRate();

    return () => {
      cancelled = true;
    };
  }, [
    view,
    mode,
    formPayrollType,
    formEmployeeId,
    selectedTimeLog?.id,
    session?.user?.id,
    currentUser?.supabase_user_id,
    teamEmployees,
  ]);

  useEffect(() => {
    if (!initialEditLogId || !onReturnToCaller) return undefined;
    if (shouldSkipSupabase(currentUser) || !session?.user?.id) {
      setCallerLogState("unavailable");
      return undefined;
    }
    let cancelled = false;

    async function openCallerLog() {
      // Same visibility as the hub: active (non-archived) rows in the actor's
      // organization scope; RLS still decides what the actor can read.
      let query = supabase
        .from("time_logs")
        .select("*")
        .eq("id", initialEditLogId)
        .limit(1);
      query = applyActiveTimelogFilter(query);
      query = await scopeOperationalTimeLogsForActor(query, supabase, currentUser, {
        directReportIds,
      });
      const { data, error } = await query;
      if (cancelled) return;
      const log = !error && Array.isArray(data) ? data[0] : null;
      if (error) console.error("TIME LOG OPEN ERROR:", error);
      if (!log || String(log.id) !== String(initialEditLogId)) {
        setCallerLogState("unavailable");
        return;
      }
      setCallerLogState("ready");
      if (canEditTimeLogRecord(log)) {
        openEditForm(log, "caller");
      } else {
        setDetailsReadOnly(true);
        openDetails(log);
      }
    }

    openCallerLog();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditLogId, session?.user?.id]);

  function openCreateForm() {
    setMode("create");
    setShowDeleteConfirm(false);
    resetFormFields();
    setView("form");
  }

  function openEditForm(log, returnTo = "hub") {
    setMode("edit");
    setShowDeleteConfirm(false);
    setFormReturnView(returnTo);
    populateFormFromLog(log);
    setView("form");
  }

  function openDetails(log) {
    setSelectedTimeLog(log);
    setShowDeleteConfirm(false);
    setView("details");
  }

  function leaveForm() {
    setShowDeleteConfirm(false);
    if (fromCaller && formReturnView === "caller") {
      returnToCaller();
      return;
    }
    setView(
      mode === "edit" && formReturnView === "details" && selectedTimeLog
        ? "details"
        : "hub",
    );
  }

  function handleSwipeBack() {
    if (view === "form") {
      leaveForm();
      return;
    }

    if (view === "details") {
      setShowDeleteConfirm(false);
      if (fromCaller) {
        returnToCaller();
        return;
      }
      setView("hub");
      return;
    }

    onBack?.();
  }

  useHardwareBackHandler(() => {
    handleSwipeBack();
    return true;
  });

  const carouselEnabled = Boolean(onCarouselNavigate) && view === "hub";
  const { ref: carouselRef, style: carouselTouchStyle } =
    useDashboardCarouselSwipe({
      onNavigate: onCarouselNavigate,
      onOpenNavigator,
      enabled: carouselEnabled,
    });

  async function handleArchiveTimeLog(logArg = null) {
    if (blockDemoSave(currentUser)) {
      return;
    }

    const targetLog = logArg?.id ? logArg : selectedTimeLog;

    if (!targetLog?.id) {
      return;
    }

    if (!window.confirm("Archive this time log?")) {
      return;
    }

    setActionLoading(true);

    const { error } = await archiveTimeLog(supabase, targetLog.id);

    setActionLoading(false);

    if (error) {
      console.error("[ArchiveTimeLog] Failed:", error);
      alert("Unable to archive time log.");
      return;
    }

    setShowDeleteConfirm(false);
    await loadTimeLogs();
    if (fromCaller) {
      returnToCaller();
      return;
    }
    setView("hub");
  }

  async function handleDeleteConfirm() {
    if (blockDemoSave(currentUser)) {
      return;
    }

    if (!selectedTimeLog?.id) {
      return;
    }

    setActionLoading(true);

    const { error } = await deleteTimeLog(supabase, selectedTimeLog.id);

    setActionLoading(false);

    if (error) {
      console.error("[DeleteTimeLog] Failed:", error);
      alert("Unable to delete time log.");
      return;
    }

    setShowDeleteConfirm(false);
    await loadTimeLogs();
    if (fromCaller) {
      returnToCaller();
      return;
    }
    setView("hub");
  }

  // New entries: explicit choice, else the org default Work Type.
  const createWorkType = resolveSessionWorkType({
    explicitChoice: workCategory,
    options: workTypeOptions,
    defaultName: defaultWorkTypeName,
  });
  const formWorkCategory = mode === "edit" ? workCategory : createWorkType;

  async function handleSubmit() {
    if (blockDemoSave(currentUser)) {
      return;
    }

    if (!session?.user?.id) {
      alert("No authenticated user found.");
      return;
    }

    if (!startDateIso || !endDateIso || !startTime || !endTime) {
      alert("Start date, start time, end date, and end time are required.");
      return;
    }

    if (!formPayrollType) {
      alert("Payroll type is required.");
      return;
    }

    if (mode !== "edit" && !createWorkType) {
      alert(workTypeOptions.length ? SELECT_WORK_TYPE_MESSAGE : NO_WORK_TYPES_MESSAGE);
      return;
    }

    const { start, end, isValid, error } = resolveManualTimelogBounds({
      startDateIso,
      endDateIso,
      startTime,
      endTime,
      originalLog: mode === "edit" ? selectedTimeLog : null,
      now: new Date(),
      rejectFuture: true,
    });

    if (!isValid) {
      alert(sessionBoundsAlertMessage(error));
      return;
    }

    const grossSeconds = Math.floor((end - start) / 1000);
    const breakSeconds = Math.max(0, Number(breakMinutes || 0) * 60);
    const netSeconds = Math.max(0, grossSeconds - breakSeconds);
    const totalHours = Number((netSeconds / 3600).toFixed(2));
    const projectFields = resolveManualTimelogProjectFields({
      formProjectId,
      projects,
      existingLog: mode === "edit" ? selectedTimeLog : null,
    });

    if (projectFields.project_id) {
      const selectedProject = projects.find(
        (project) => project.project_id === projectFields.project_id,
      );
      try {
        assertProjectOperationallyWritable(
          selectedProject || {
            project_id: projectFields.project_id,
            client_id: projectFields.client_id,
            is_active: true,
            status: "active",
          },
          selectedProject?.clients,
        );
      } catch (lockError) {
        alert(lockError?.message || OPERATIONAL_LOCK_USER_MESSAGE);
        return;
      }
    }

    try {
      await runWithSavingState(setSaving, async () => {
        if (mode === "edit" && selectedTimeLog) {
          const rateActor = actorFromTimelogRow(selectedTimeLog);
          const employeeSnapshot = await resolveTimelogEmployeeSnapshot(
            rateActor,
            {
              supabase,
              payrollType: formPayrollType,
              supabaseUserId:
                selectedTimeLog.supabase_user_id ||
                selectedTimeLog.user_id ||
                null,
            },
          );

          const projectChanged =
            toNullableUuid(selectedTimeLog.project_id) !==
            projectFields.project_id;

          const updatePayload = await applyQuoteLineLinkToTimelogRow({
            ...projectFields,
            date_iso: startDateIso,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            total_hours: totalHours,
            total_seconds: grossSeconds,
            break_seconds: breakSeconds,
            net_work_seconds: netSeconds,
            work_completed: workCompleted || "",
            work_category: workCategory || "",
            notes: notes || "",
            payroll_type: employeeSnapshot.payroll_type || formPayrollType,
            hourly_rate: employeeSnapshot.hourly_rate,
            classification_name:
              selectedTimeLog.classification_name ||
              employeeSnapshot.classification_name ||
              "",
            trade_id: toNullableUuid(
              selectedTimeLog.trade_id ?? employeeSnapshot.trade_id,
            ),
            trade_name:
              selectedTimeLog.trade_name || employeeSnapshot.trade_name || "",
            quote_line_snapshot_id: projectChanged
              ? null
              : toNullableUuid(selectedTimeLog.quote_line_snapshot_id),
            updated_at: new Date().toISOString(),
          });

          const { error } = await supabase
            .from("time_logs")
            .update(updatePayload)
            .eq("id", selectedTimeLog.id);

          if (error) {
            console.error("TIME LOG UPDATE ERROR:", error);
            alert(error.message || "Unable to update time log.");
            return;
          }

          setSelectedTimeLog({
            ...selectedTimeLog,
            ...updatePayload,
            id: selectedTimeLog.id,
            session_id: selectedTimeLog.session_id,
            supabase_user_id:
              selectedTimeLog.supabase_user_id || selectedTimeLog.user_id,
            employee: selectedTimeLog.employee,
          });
          setSubmitted(true);
          return;
        }

        const createActor = resolveCreateEmployeeActor(formEmployeeId);
        const employeeSnapshot = await resolveTimelogEmployeeSnapshot(
          createActor,
          {
            supabase,
            payrollType: formPayrollType,
            supabaseUserId:
              createActor?.supabase_user_id ||
              createActor?.user_id ||
              session.user.id,
          },
        );

        if (employeeSnapshot.missingCustomRate) {
          alert(
            "Selected employee has no User Rate. Set their hourly rate in Administration or choose another payroll type.",
          );
          return;
        }

        const insertPayload = await applyQuoteLineLinkToTimelogRow({
          session_id: `MANUAL-${Date.now()}`,
          ...snapshotToTimelogFields(employeeSnapshot),
          ...projectFields,
          date_iso: startDateIso,
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          total_hours: totalHours,
          total_seconds: grossSeconds,
          break_seconds: breakSeconds,
          net_work_seconds: netSeconds,
          work_completed: workCompleted || "",
          work_category: createWorkType,
          notes: notes || "",
          approval_status: "pending",
          source: "manual_entry",
          request_source: "time_logs_hub",
        });

        const { data: createdRows, error } = await orgScopedInsert(
          supabase,
          "time_logs",
          [insertPayload],
          currentUser,
          { select: "*", single: true },
        );

        if (error) {
          console.error("TIME LOG CREATE ERROR:", error);
          alert(error.message || "Unable to create time log.");
          return;
        }

        const createdLog = createdRows;
        if (createdLog?.id) {
          setMode("edit");
          populateFormFromLog(createdLog);
          setSelectedTimeLog(createdLog);
          await loadTimeLogs();
          setSubmitted(true);
          return;
        }

        setSubmitted(true);
      });
    } catch (error) {
      console.error("TIME LOG SAVE ERROR:", error);
      alert(error.message || "Unable to save time log.");
    }
  }

  const swipeHandlers = {
    onTouchStart: (e) => {
      if (e.touches.length !== 1) return;
      touchStartX.current = e.touches[0].clientX;
    },
    onTouchEnd: (e) => {
      if (touchStartX.current === null) return;

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;

      if (Math.abs(deltaX) > 80) {
        handleSwipeBack();
      }

      touchStartX.current = null;
    },
  };

  if (submitted) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <div className="ci-success-icon">
            <svg
              viewBox="0 0 52 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle nx-checkmark-circle"
                cx="26"
                cy="26"
                r="24"
              />
              <path
                className="checkmark-check nx-checkmark-check"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>

          <h1 className="ci-success-title">
            {mode === "edit" ? "Time Log Updated" : "Time Log Created"}
          </h1>

          <p className="ci-success-sub">
            Your time log has been saved successfully.
            {mode === "edit" && selectedTimeLog?.id
              ? " You can add materials used during this work session."
              : ""}
          </p>

          {mode === "edit" && selectedTimeLog?.id ? (
            <button
              className="nx-btn nx-btn-gold"
              style={{ marginTop: "12px" }}
              onClick={() => {
                setSubmitted(false);
                setView("form");
              }}
            >
              Continue to Materials
            </button>
          ) : null}

          <button
            className={
              mode === "edit" && selectedTimeLog?.id
                ? "nx-btn nx-btn-ghost"
                : "nx-btn nx-btn-gold"
            }
            style={{ marginTop: "12px" }}
            onClick={() => {
              setSubmitted(false);
              if (fromCaller) {
                returnToCaller();
                return;
              }
              loadTimeLogs();
              setView("hub");
            }}
          >
            {fromCaller ? "Back to Project" : "Back to Time Logs"}
          </button>
        </div>
      </div>
    );
  }

  const defaultDateRange = "this_month";
  const scopeOptions = showScopeSelector
    ? [
        { value: TIMELOG_HUB_SCOPE.MY_TIME, label: "My Time" },
        ...(canUseTimelogHubScope(currentUser, TIMELOG_HUB_SCOPE.MY_TEAM, {
          directReportIds,
        })
          ? [{ value: TIMELOG_HUB_SCOPE.MY_TEAM, label: "My Team" }]
          : []),
        ...(showOrgScope
          ? [{ value: TIMELOG_HUB_SCOPE.ORGANIZATION, label: "Organization" }]
          : []),
      ]
    : [];

  const projectFilterName = projects.find(
    (project) => project.project_id === hubProjectFilter,
  )?.project_name;
  const employeeFilterName = teamEmployees.find(
    (employee) => employee.supabase_user_id === hubEmployeeFilter,
  )?.employee_name;
  const dateRangeLabel = TIME_LOG_DATE_RANGE_OPTIONS.find(
    (option) => option.value === dateRangeFilter,
  )?.label;

  const activeFilterChips = [
    ...(hubProjectFilter
      ? [
          {
            key: "project",
            label: projectFilterName || "Project",
            onClear: () => setHubProjectFilter(""),
          },
        ]
      : []),
    ...(showEmployeeFilter && hubEmployeeFilter
      ? [
          {
            key: "employee",
            label: employeeFilterName || "Employee",
            onClear: () => setHubEmployeeFilter(""),
          },
        ]
      : []),
    ...(dateRangeFilter !== defaultDateRange
      ? [
          {
            key: "range",
            label: dateRangeLabel || "Date range",
            onClear: () => setDateRangeFilter(defaultDateRange),
          },
        ]
      : []),
  ];

  function clearHubFilters() {
    setHubProjectFilter("");
    setHubEmployeeFilter("");
    setDateRangeFilter(defaultDateRange);
  }

  const filterSheetFields = [
    {
      key: "project",
      label: "Project",
      value: hubProjectFilter,
      onChange: setHubProjectFilter,
      options: [
        { value: "", label: "All Projects" },
        ...projects.map((project) => ({
          value: project.project_id,
          label: project.project_name,
        })),
      ],
    },
    {
      key: "range",
      label: "Date Range",
      value: dateRangeFilter,
      onChange: setDateRangeFilter,
      options: TIME_LOG_DATE_RANGE_OPTIONS,
    },
    ...(showEmployeeFilter
      ? [
          {
            key: "employee",
            label: "Employee",
            value: hubEmployeeFilter,
            onChange: setHubEmployeeFilter,
            options: [
              { value: "", label: "All Employees" },
              ...teamEmployees.map((employee) => ({
                value: employee.supabase_user_id,
                label: employee.employee_name || "Unknown Employee",
              })),
            ],
          },
        ]
      : []),
  ];

  const deleteConfirmModal =
    showDeleteConfirm && selectedTimeLog ? (
      <ViewportPortal>
      <div style={MODAL_OVERLAY_STYLE}>
        <div style={MODAL_PANEL_STYLE}>
          <h2 style={{ marginTop: 0, marginBottom: "12px" }}>Are you sure?</h2>

          <p style={{ opacity: 0.75, lineHeight: 1.5 }}>
            Deleting a time log may affect payroll, reports, and historical
            records.
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "20px",
            }}
          >
            <button
              className="nx-btn nx-btn-gold"
              disabled={actionLoading}
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </button>

            <button
              className="nx-btn nx-btn-ghost"
              disabled={actionLoading}
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      </ViewportPortal>
    ) : null;

  if (view === "hub" && fromCaller) {
    return (
      <CrewPilotModuleShell
        title="Time Log"
        subtitle={callerLogState === "unavailable" ? "Not available" : "Opening time log"}
        onBack={returnToCaller}
        layout={PAGE_LAYOUT.COMMAND}
        compact
      >
        <p
          data-testid="time-log-caller-state"
          role={callerLogState === "unavailable" ? "alert" : "status"}
          style={{ textAlign: "center", marginTop: "24px", color: "var(--nx-text-muted, #5b6472)" }}
        >
          {callerLogState === "unavailable"
            ? "This time log is not available. It may have been archived, or you may not have access to it."
            : "Opening time log…"}
        </p>
      </CrewPilotModuleShell>
    );
  }

  if (view === "hub") {
    const summary = summarizeLogs(filteredLogs);
    const rowMenuItems = (log) => [
      { key: "view", label: "View details", onSelect: openDetails },
      { key: "edit", label: "Edit", onSelect: (target) => openEditForm(target, "hub") },
      ...(canEditTeamLogs
        ? [
            {
              key: "archive",
              label: "Archive",
              danger: true,
              onSelect: (target) => handleArchiveTimeLog(target),
            },
          ]
        : []),
    ];
    const showRowEmployee =
      logScope !== TIMELOG_HUB_SCOPE.MY_TIME &&
      !hubEmployeeFilter &&
      !shouldGroupByEmployee;
    const filtersActive = activeFilterChips.length > 0;

    if (isDesktopViewport) {
      return (
        <div ref={carouselRef}>
          <TimeLogsDesktop
            onBack={onBack}
            scopeOptions={scopeOptions}
            logScope={logScope}
            onScopeChange={setLogScope}
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            filterChips={activeFilterChips}
            onOpenFilters={() => setFilterSheetOpen(true)}
            onClearFilters={clearHubFilters}
            logs={filteredLogs}
            groupedByEmployee={shouldGroupByEmployee ? groupedLogs : null}
            showEmployee={showRowEmployee}
            onOpen={openDetails}
            onNew={openCreateForm}
            menuItemsFor={rowMenuItems}
          />
          {filterSheetOpen ? (
            <TimeLogsFilterSheet
              fields={filterSheetFields}
              hasActive={filtersActive}
              onClearAll={clearHubFilters}
              onClose={() => setFilterSheetOpen(false)}
            />
          ) : null}
        </div>
      );
    }

    return (
      <div
        ref={carouselRef}
        style={carouselTouchStyle}
        {...(carouselEnabled ? {} : swipeHandlers)}
      >
        <CrewPilotModuleShell
          title="Time Logs"
          subtitle="Search, review, and manage employee time"
          onBack={onBack}
          layout={PAGE_LAYOUT.COMMAND}
          primary
          compact
        >
          <TimeLogsScopeTabs
            options={scopeOptions}
            value={logScope}
            onChange={setLogScope}
          />

          <TimeLogsSearchRow
            search={searchTerm}
            onSearch={setSearchTerm}
            filterCount={activeFilterChips.length}
            onOpenFilters={() => setFilterSheetOpen(true)}
          />

          <TimeLogsActiveFilters
            chips={activeFilterChips}
            onClearAll={clearHubFilters}
          />

          <TimeLogsActionBar
            summary={`${pluralLogs(summary.count)} · ${summary.durationLabel}`}
            onNew={openCreateForm}
          />

          <div>
            {filteredLogs.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  color: "var(--nx-text-muted, #5b6472)",
                }}
              >
                {searchTerm || filtersActive
                  ? "No time logs match your search and filters."
                  : "No time logs found."}
              </p>
            ) : (
              <TimeLogList
                logs={filteredLogs}
                groupByEmployee={shouldGroupByEmployee ? groupedLogs : null}
                onOpen={openDetails}
                menuItemsFor={rowMenuItems}
                rowProps={{ showEmployee: showRowEmployee }}
              />
            )}
          </div>
        </CrewPilotModuleShell>

        {filterSheetOpen ? (
          <TimeLogsFilterSheet
            fields={filterSheetFields}
            hasActive={filtersActive}
            onClearAll={clearHubFilters}
            onClose={() => setFilterSheetOpen(false)}
          />
        ) : null}
      </div>
    );
  }

  if (view === "details" && selectedTimeLog) {
    return (
      <div {...swipeHandlers}>
        <TimeLogDetails
          log={selectedTimeLog}
          canEdit={!detailsReadOnly}
          canArchive={!detailsReadOnly && canEditTeamLogs}
          canDelete={!detailsReadOnly && canEditTeamLogs && canDeleteLogs}
          busy={actionLoading}
          onBack={() => (fromCaller ? returnToCaller() : setView("hub"))}
          onEdit={() => openEditForm(selectedTimeLog, "details")}
          onArchive={() => handleArchiveTimeLog(selectedTimeLog)}
          onDelete={() => setShowDeleteConfirm(true)}
        />
        {deleteConfirmModal}
      </div>
    );
  }

  const netHoursPreview = computeNetHoursPreview({
    startDateIso,
    endDateIso,
    startTime,
    endTime,
    breakMinutes,
    originalLog: mode === "edit" ? selectedTimeLog : null,
  });

  const employeeOptions = [
    {
      value: currentUser?.supabase_user_id || "",
      label: currentUser?.employee_name || "Me",
    },
    ...teamEmployees
      .filter(
        (employee) => employee.supabase_user_id !== currentUser?.supabase_user_id,
      )
      .map((employee) => ({
        value: employee.supabase_user_id,
        label: employee.employee_name || "Unknown Employee",
      })),
  ];

  // A log can point at a project that is no longer in the active list
  // (archived/locked): keep it selectable so the form never silently shows
  // "None Selected" for a log that has one.
  const projectSelectOptions =
    formProjectId && !projects.some((project) => project.project_id === formProjectId)
      ? [
          ...projects,
          {
            project_id: formProjectId,
            project_name: selectedTimeLog?.project_name || "Current project",
          },
        ]
      : projects;

  const payrollSelectOptions = [
    ...(!payrollTypeOptions.some((option) => option.value === formPayrollType) &&
    formPayrollType
      ? [{ value: formPayrollType, label: formPayrollType }]
      : []),
    ...payrollTypeOptions,
  ];

  return (
    <div {...swipeHandlers}>
      <TimeLogEditor
        mode={mode}
        saving={saving}
        actionLoading={actionLoading}
        selectedLog={mode === "edit" ? selectedTimeLog : null}
        canArchive={mode === "edit" && canEditTeamLogs}
        canDelete={mode === "edit" && canEditTeamLogs && canDeleteLogs}
        values={{
          projectId: formProjectId,
          employeeId: formEmployeeId,
          employeeName: formEmployeeName,
          startDateIso,
          endDateIso,
          startTime,
          endTime,
          breakMinutes,
          netHoursPreview,
          tradeName: formTradeName,
          classificationName: formClassificationName,
          payrollType: formPayrollType,
          hourlyRate: formHourlyRate,
          rateResolving,
          workCategory: formWorkCategory,
          workCompleted,
          notes,
        }}
        options={{
          projects: projectSelectOptions,
          canAssignEmployee: canAssignEmployeeOnCreate,
          employeeOptions,
          categoryOptions: buildTimeLogWorkTypeOptions(
            workTypeOptions,
            formWorkCategory,
            mode,
          ),
          payrollOptions: payrollSelectOptions,
        }}
        on={{
          projectId: setFormProjectId,
          employeeId: handleCreateEmployeeChange,
          startDateIso: setStartDateIso,
          endDateIso: setEndDateIso,
          startTime: setStartTime,
          endTime: setEndTime,
          breakMinutes: setBreakMinutes,
          payrollType: setFormPayrollType,
          workCategory: setWorkCategory,
          workCompleted: setWorkCompleted,
          notes: setNotes,
          save: handleSubmit,
          cancel: leaveForm,
          archive: () => handleArchiveTimeLog(selectedTimeLog),
          requestDelete: () => setShowDeleteConfirm(true),
        }}
      />
      {mode === "edit" ? deleteConfirmModal : null}
    </div>
  );
}
